import { useRef, useState } from "react";
import { useFormContext } from "react-hook-form";

interface SingleImageInputProps {
  name: string;
  renameTemplate: string;
  dynamicValues?: Record<string, any>;
  onUploadComplete?: (url: string) => void;
}

export function SingleImageInput({
  name,
  renameTemplate,
  dynamicValues = {},
  onUploadComplete,
}: SingleImageInputProps) {
  const { setValue, getValues } = useFormContext();
  const inputRef = useRef<HTMLInputElement>(null);

  const currentValue = getValues(name);
  const [preview, setPreview] = useState<string | null>(currentValue || null);
  const [uploading, setUploading] = useState(false);

  /* ---------------- Rename helper ---------------- */

  const buildFileName = (file: File) => {
    const ext = file.name.split(".").pop();
    return renameTemplate.replace(/\{(.*?)\}/g, (_, key) =>
      dynamicValues[key] ?? "x"
    ).replace(/\.\w+$/, `.${ext}`);
  };

  /* ---------------- Upload handler ---------------- */

  const handleSelect = async (file: File) => {
    setUploading(true);

    const renamedFile = new File(
      [file],
      buildFileName(file),
      { type: file.type }
    );

    // 🔥 Replace this with your real upload logic
    const uploadedUrl = await fakeUpload(renamedFile);

    setPreview(uploadedUrl);
    setValue(name, uploadedUrl, { shouldDirty: true });
    onUploadComplete?.(uploadedUrl);

    setUploading(false);
  };

  /* ---------------- Remove ---------------- */

  const removeImage = () => {
    setPreview(null);
    setValue(name, undefined, { shouldDirty: true });
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="w-full space-y-2">
      {preview ? (
        <div className="relative w-full max-w-xs">
          <img
            src={preview}
            className="rounded-lg border border-gray-300"
          />
          <button
            type="button"
            onClick={removeImage}
            className="absolute top-2 right-2 bg-red-600 text-white text-xs px-2 py-1 rounded"
          >
            Remove
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 text-gray-500 hover:border-blue-500"
        >
          {uploading ? "Uploading..." : "Click to upload image"}
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleSelect(file);
        }}
      />
    </div>
  );
}

/* ---------------- Mock uploader ---------------- */

async function fakeUpload(file: File): Promise<string> {
  await new Promise((r) => setTimeout(r, 800));
  return URL.createObjectURL(file); // replace with real URL
}